import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  MagnifyingGlass,
  VideoCamera,
  Brain,
  FileText,
  BookOpen,
  X,
  SpinnerGap,
  ArrowRight
} from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { canEditContent } from "../utils/permissions";
import { loadRecentIdSearches, rememberIdSearch, forgetIdSearch } from "../lib/recentIdSearches";
import RecentIdSearches from "./RecentIdSearches";

const TYPE_COLORS = {
  qa: "bg-blue-100 text-blue-700",
  procedure: "bg-blue-100 text-blue-700",
  script: "bg-yellow-100 text-yellow-700",
  objection: "bg-red-100 text-red-700",
  rebuttal: "bg-purple-100 text-purple-700",
  concept: "bg-indigo-100 text-indigo-700",
};

export default function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();
  // Tâche 1304 — Le libellé annonce la recherche par identifiant uniquement
  // aux personnes autorisées à concevoir ou corriger (c'est le seul public
  // pour qui le serveur la fait).
  const { user } = useAuth();
  const { tr } = useLang();
  const peutConcevoir = canEditContent(user);
  // Tâche 1305 — Les derniers identifiants cherchés, sous le champ.
  const [recentIds, setRecentIds] = useState(() => loadRecentIdSearches());
  const [focused, setFocused] = useState(false);

  // Cmd/Ctrl+Maj+F shortcut (Ctrl+K est réservé à la barre d'actions, tâche 1287)
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "f") {
        // Tâche 1287 — Ctrl+K est désormais la barre d'ACTIONS ; la recherche
        // de contenu passe sur Ctrl+Maj+F.
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) { setResults(null); setOpen(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/api/search`, {
          params: { q: query },
          withCredentials: true,
        });
        setResults(data);
        setOpen(true);
        // Tâche 1305 — On retient l'identifiant SEULEMENT quand c'est lui qui
        // a trouvé la vidéo (`matched_by_id`), sinon la liste se remplirait
        // de recherches par titre.
        const parId = (data.videos || []).find((v) => v.matched_by_id);
        if (parId) setRecentIds(rememberIdSearch({ id: parId.id, title: parId.title }));
      } catch {
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [query]);

  const totalResults = results
    ? (results.videos?.length || 0) + (results.knowledge?.length || 0) +
      (results.segments?.length || 0) + (results.dictionary?.length || 0)
    : 0;

  const goTo = (path) => {
    setOpen(false);
    setQuery("");
    navigate(path);
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          ref={inputRef}
          type="search"
          placeholder={peutConcevoir
            ? tr("Recherche globale (vidéos, identifiant, Q&R, transcripts, dictionnaire)…",
                 "Global search (videos, identifier, Q&A, transcripts, dictionary)…")
            : tr("Recherche globale (vidéos, Q&R, transcripts, dictionnaire)…",
                 "Global search (videos, Q&A, transcripts, dictionary)…")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { setFocused(true); if (results && query.length >= 2) setOpen(true); }}
          className="h-9 w-72 xl:w-96 pl-9 pr-16 bg-zinc-100 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] transition-all"
          data-testid="global-search-input"
        />
        {!query && (
          <kbd className="hidden xl:flex absolute right-3 top-1/2 -translate-y-1/2 items-center gap-0.5 text-[10px] font-mono text-zinc-600 bg-white border border-zinc-200 rounded px-1.5 py-0.5 pointer-events-none">
            ⌘ ⇧ F
          </kbd>
        )}
        {query && (
          <button
            onClick={() => { setQuery(""); setResults(null); setOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
            data-testid="global-search-clear"
          >
            {loading ? <SpinnerGap size={14} className="animate-spin" /> : <X size={14} />}
          </button>
        )}
      </div>

      {/* Tâche 1305 (Eric 2026-06-30) — Les derniers identifiants cherchés,
          sous le champ, dès qu'on s'y place et qu'il est vide. */}
      {peutConcevoir && focused && !open && query.trim().length < 2 && recentIds.length > 0 && (
        <div
          className="absolute top-full left-0 mt-2 bg-white border border-zinc-200 rounded-lg shadow-xl px-3 py-2 z-50 w-max max-w-[32rem]"
          data-testid="global-search-recent-panel"
        >
          <RecentIdSearches
            items={recentIds}
            onPick={(entry) => { setQuery(entry.id); inputRef.current?.focus(); }}
            onForget={(id) => setRecentIds(forgetIdSearch(id))}
            testidPrefix="global-recent-id"
          />
        </div>
      )}

      {/* Results Dropdown */}
      {open && results && (
        <div
          className="absolute top-full left-0 right-0 mt-2 bg-white border border-zinc-200 rounded-lg shadow-xl overflow-hidden z-50 max-h-[70vh] overflow-y-auto"
          data-testid="search-results-dropdown"
        >
          {totalResults === 0 ? (
            <div className="p-6 text-center text-zinc-400 text-sm">
              Aucun résultat pour "{query}"
            </div>
          ) : (
            <>
              {/* Tâche 1132 (Eric 2026-06-09) — Dictionnaire EN PREMIER : taper
                  « cashdown » doit montrer la fiche « mise de fonds » tout de
                  suite, pas après cinq transcripts. */}
              {results.dictionary?.length > 0 && (
                <div>
                  <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-100 flex items-center gap-2">
                    <BookOpen size={14} className="text-zinc-500" />
                    <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                      Dictionnaire ({results.dictionary.length})
                    </span>
                  </div>
                  {results.dictionary.map((d) => (
                    <button
                      key={d.id}
                      onClick={() => goTo(`/dashboard/dictionnaire?focus=${encodeURIComponent(d.id)}`)}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 transition-colors text-left"
                      data-testid={`search-dictionary-${d.id}`}
                    >
                      <div className="w-8 h-8 bg-zinc-100 rounded flex items-center justify-center flex-shrink-0">
                        <BookOpen size={16} className="text-zinc-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{d.term}</p>
                        {d.matched_via ? (
                          <p
                            className="text-xs text-emerald-700"
                            data-testid={`search-dictionary-via-${d.id}`}
                          >
                            trouvé via « {d.matched_via} »
                          </p>
                        ) : (
                          <p className="text-xs text-zinc-400 truncate">{d.definition}</p>
                        )}
                      </div>
                      <ArrowRight size={14} className="text-zinc-300" />
                    </button>
                  ))}
                </div>
              )}

              {/* Videos */}
              {results.videos?.length > 0 && (
                <div>
                  <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-100 flex items-center gap-2">
                    <VideoCamera size={14} className="text-zinc-500" />
                    <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Videos ({results.videos.length})</span>
                  </div>
                  {results.videos.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => goTo(`/dashboard/video/${v.id}`)}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 transition-colors text-left"
                      data-testid={`search-video-${v.id}`}
                    >
                      <div className="w-8 h-8 bg-zinc-100 rounded flex items-center justify-center flex-shrink-0">
                        <VideoCamera size={16} className="text-zinc-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{v.title}</p>
                        {/* Tâche 1304 — Trouvée par son identifiant : on
                            l'affiche, sinon rien ne confirme qu'on est tombé
                            sur la bonne vidéo. */}
                        {v.matched_by_id ? (
                          <p
                            className="text-[10px] font-mono text-[#002FA7] truncate"
                            data-testid={`search-video-matched-id-${v.id}`}
                            title="Trouvée par son identifiant"
                          >
                            {v.id}
                          </p>
                        ) : (
                          <p className="text-xs text-zinc-400">{v.training_theme}</p>
                        )}
                      </div>
                      <ArrowRight size={14} className="text-zinc-300" />
                    </button>
                  ))}
                </div>
              )}

              {/* Knowledge */}
              {results.knowledge?.length > 0 && (
                <div>
                  <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-100 flex items-center gap-2">
                    <Brain size={14} className="text-zinc-500" />
                    <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Connaissances ({results.knowledge.length})</span>
                  </div>
                  {results.knowledge.map((k) => (
                    <button
                      key={k.id}
                      onClick={() => goTo(
                        // Vincent P1 (Eric 2026-08-05 SPEC STRICK) — Focus mécanique
                        // sur les résultats Cmd+K : on passe `?tab=<type>&focus=<id>`
                        // pour que `KnowledgePage` bascule sur l'onglet du type ET
                        // que `useFocusFromURL` scroll/highlight la carte cible.
                        `/dashboard/knowledge?tab=${encodeURIComponent(k.type || "all")}&focus=${encodeURIComponent(k.id)}`
                      )}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 transition-colors text-left"
                      data-testid={`search-knowledge-${k.id}`}
                    >
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${TYPE_COLORS[k.type] || "bg-zinc-100 text-zinc-600"}`}>
                        {k.type}
                      </span>
                      <p className="text-sm flex-1 truncate">{k.title}</p>
                      <ArrowRight size={14} className="text-zinc-300" />
                    </button>
                  ))}
                </div>
              )}

              {/* Transcript Segments */}
              {results.segments?.length > 0 && (
                <div>
                  <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-100 flex items-center gap-2">
                    <FileText size={14} className="text-zinc-500" />
                    <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Transcripts ({results.segments.length})</span>
                  </div>
                  {results.segments.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => goTo(
                        // Vincent P1 (Eric 2026-08-05 SPEC STRICK) — Focus mécanique
                        // sur segment : `?tab=transcript&t=<start>&focus=segment-t-<start>`.
                        // Le `data-focus-target` des segments est indexé par
                        // `Math.floor(segment.start)` (stable entre `transcript_segments`
                        // et `video.segments`). `?t=` déclenche le seek + auto-scroll.
                        `/dashboard/video/${s.video_id}?tab=transcript&t=${Math.floor(s.start || 0)}&focus=${encodeURIComponent(`segment-t-${Math.floor(s.start || 0)}`)}`
                      )}
                      className="w-full px-4 py-3 hover:bg-zinc-50 transition-colors text-left"
                      data-testid={`search-segment-${i}`}
                    >
                      <p className="text-xs text-zinc-400 mb-0.5">{s.video_title} - {formatTime(s.start)}</p>
                      <p className="text-sm text-zinc-700 line-clamp-2">{s.text}</p>
                    </button>
                  ))}
                </div>
              )}

              <div className="px-3 py-2 bg-zinc-50 border-t border-zinc-100 text-center">
                <span className="text-xs text-zinc-400">{totalResults} resultat{totalResults !== 1 ? "s" : ""}</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function formatTime(seconds) {
  if (!seconds) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}
